import { DEFAULT_CONFIG } from '../shared/config.js';
import { PLAYER_COLOR_CSS } from '../shared/colors.js';

interface TrailPoint {
  x: number;
  y: number;
  t: number;
}

interface Trail {
  id: string;
  colorSlot: number;
  points: TrailPoint[];
  /** First live index (avoids splicing every frame). */
  head: number;
  ended: boolean;
  endAt: number | null;
  fadeMs: number;
  isDebris: boolean;
}

/** Alpha buckets for batched strokes (no per-segment shadowBlur). */
const ALPHA_BUCKETS = 10;
/** Min world-space distance between stored trail samples. */
const MIN_POINT_DIST = 0.4;
/**
 * Hard cap per trail — must cover a full maxlen flight at dense substep
 * sampling (maxSegments × segmentSteps), otherwise the head is chopped
 * and the trail looks like Snake instead of fading.
 */
const MAX_POINTS =
  DEFAULT_CONFIG.maxSegments * DEFAULT_CONFIG.segmentSteps + 64;

/**
 * Time-based trails (same path for multiplayer + single-player).
 */
export class TrailSystem {
  private trails = new Map<string, Trail>();
  private normalFadeMs = DEFAULT_CONFIG.trailFadeSec * 1000;
  private debrisFadeMs = DEFAULT_CONFIG.debrisTrailFadeSec * 1000;
  private spMode = false;

  setSinglePlayer(on: boolean): void {
    this.spMode = on;
  }

  private colorFor(slot: number): string {
    if (this.spMode) return slot === 0 ? '#ff2a2a' : '#3dff6a';
    return PLAYER_COLOR_CSS[slot % PLAYER_COLOR_CSS.length];
  }

  spawn(id: string, colorSlot: number, x: number, y: number, isDebris = false): void {
    const now = performance.now();
    this.trails.set(id, {
      id,
      colorSlot,
      points: [{ x, y, t: now }],
      head: 0,
      ended: false,
      endAt: null,
      fadeMs: isDebris ? this.debrisFadeMs : this.normalFadeMs,
      isDebris,
    });
  }

  push(id: string, x: number, y: number, colorSlot?: number, isDebris = false): void {
    let trail = this.trails.get(id);
    const now = performance.now();
    if (!trail) {
      this.spawn(id, colorSlot ?? 0, x, y, isDebris);
      return;
    }
    if (trail.ended) return;
    const last = trail.points[trail.points.length - 1];
    if (last && Math.hypot(last.x - x, last.y - y) < MIN_POINT_DIST) {
      // Do not drag the tip — that creates false chords / hooks
      return;
    }
    trail.points.push({ x, y, t: now });
    if (trail.points.length - trail.head > MAX_POINTS) {
      trail.head = trail.points.length - MAX_POINTS;
    }
  }

  end(id: string, x?: number, y?: number): void {
    const trail = this.trails.get(id);
    if (!trail) return;
    if (x !== undefined && y !== undefined) {
      const last = trail.points[trail.points.length - 1];
      if (!last || Math.hypot(last.x - x, last.y - y) > 0.05) {
        trail.points.push({ x, y, t: performance.now() });
      } else {
        last.x = x;
        last.y = y;
        last.t = performance.now();
      }
    }
    if (!trail.ended) {
      trail.ended = true;
      trail.endAt = performance.now();
    }
  }

  private prune(now: number): void {
    for (const [id, trail] of this.trails) {
      while (
        trail.head < trail.points.length &&
        now - trail.points[trail.head].t >= trail.fadeMs
      ) {
        trail.head++;
      }
      if (trail.head > 64 && trail.head * 2 > trail.points.length) {
        trail.points = trail.points.slice(trail.head);
        trail.head = 0;
      }
      if (trail.head >= trail.points.length) {
        if (trail.ended) this.trails.delete(id);
        else if (trail.endAt && now - trail.endAt > trail.fadeMs) this.trails.delete(id);
      }
    }
  }

  draw(
    ctx: CanvasRenderingContext2D,
    worldToScreen: (x: number, y: number) => { x: number; y: number },
  ): void {
    const now = performance.now();
    this.prune(now);

    const batches = new Map<number, { color: string; alpha: number; coords: number[] }>();

    for (const trail of this.trails.values()) {
      const start = trail.head;
      if (trail.points.length - start < 2) continue;
      const color = this.colorFor(trail.colorSlot);

      for (let i = start + 1; i < trail.points.length; i++) {
        const a = trail.points[i - 1];
        const b = trail.points[i];
        const age = now - b.t;
        const alpha = Math.max(0, 1 - age / trail.fadeMs);
        if (alpha <= 0.03) continue;
        const bucket = Math.min(ALPHA_BUCKETS - 1, Math.floor(alpha * ALPHA_BUCKETS));
        const key = trail.colorSlot * ALPHA_BUCKETS + bucket;
        let batch = batches.get(key);
        if (!batch) {
          batch = {
            color,
            alpha: (bucket + 0.55) / ALPHA_BUCKETS,
            coords: [],
          };
          batches.set(key, batch);
        }
        const sa = worldToScreen(a.x, a.y);
        const sb = worldToScreen(b.x, b.y);
        batch.coords.push(sa.x, sa.y, sb.x, sb.y);
      }
    }

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowBlur = 0;

    for (const batch of batches.values()) {
      const coords = batch.coords;
      if (coords.length < 4) continue;
      ctx.strokeStyle = batch.color;
      ctx.globalAlpha = batch.alpha * 0.95;
      ctx.lineWidth = 1.35 + batch.alpha * 0.4;
      ctx.beginPath();
      for (let i = 0; i < coords.length; i += 4) {
        ctx.moveTo(coords[i], coords[i + 1]);
        ctx.lineTo(coords[i + 2], coords[i + 3]);
      }
      ctx.stroke();
    }

    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;
  }

  clear(): void {
    this.trails.clear();
  }
}
